import React, { useEffect, useState, useContext, useCallback } from 'react';
import { Grid } from '@mui/material';
import Loading from "../../components/Loading/Loading";
import { getOfertasPaginate } from "../../services/OfertasServices";
import { ProductList } from '../../components/ProductList/ProductList';
import ControlsRow from '../../components/ControlsRows/ControlsRows';
import { pdf } from '@react-pdf/renderer';
import ExportPdf from '../../components/pdf/exportPdf';
import { AuthContext } from "../../context/authContext";
import { getCarritoByID } from "../../services/CarritoServices";
import logoDefault from "../../images/productDefault.jpg";
import { CategoryFilter } from '../../components/CategoryFilter/CategoryFilter';
import { useSearchParams } from 'react-router-dom';
import { useFilterParams } from '../../hooks/useFilterParams';

const getOrder = (ob) => {
  switch (ob) {
    case "precio_asc": return { orderBy: 'aofd_ofertaprecio', orden: 'asc' };
    case "precio_desc": return { orderBy: 'aofd_ofertaprecio', orden: 'desc' };
    case "nombre_asc": return { orderBy: 'art_descripcion', orden: 'asc' };
    case "nombre_desc": return { orderBy: 'art_descripcion', orden: 'desc' };
    case "codigo_asc": return { orderBy: 'art_codigo', orden: 'asc' };
    case "codigo_desc": return { orderBy: 'art_codigo', orden: 'desc' };
    default: return { orderBy: '', orden: '' };
  }
};

const OfertasResults = () => {
  const [searchParams] = useSearchParams();
  const ofertasId = searchParams.get('id') ?? '';

  const { empresa, user = {} } = useContext(AuthContext);

  const {
  searchTerm,
  selectedRamoId, selectedRubroId, selectedSubrubroId, selectedMarcaId,
  currentPage, perPage, orderBy,
  setRamoId, setRubroId, setSubrubroId, setMarcaId,
  setSearch, setCurrentPage, setPerPage, setOrderBy,
  clearAll,
} = useFilterParams();

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const [viewMode, setViewMode] = useState('horizontal');

  useEffect(() => {
    setViewMode(empresa?.vista === false ? 'horizontal' : 'grid');
  }, [empresa]);

  useEffect(() => { fetchCarrito(); }, []);

  const fetchCarrito = async () => {
    const idCarrito = localStorage.getItem('idCarrito');
    if (!idCarrito) return;
    try {
      const res = await getCarritoByID(idCarrito);
      if (res?.Carrito_items) {
        setCartItems(res.Carrito_items.map(item => ({
          cartId: res.id, itemId: item.id, artId: item.art_Id,
        })));
      }
    } catch (e) { console.error(e); }
  };

  const getImageSrc = useCallback((producto) => {
    const a = producto?.oferta_Art?.art_imagen?.trim();
    const e = empresa?.imagen_articulo?.trim();
    if (a) return `${process.env.REACT_APP_URL_BACKEND}${a}`;
    if (e) return `${process.env.REACT_APP_URL_BACKEND}${e}`;
    return logoDefault;
  }, [empresa]);

  const mapProducto = useCallback((producto) => {
    const stockFormatted = producto?.stock != null
      ? Number.isInteger(producto.stock)
        ? producto.stock.toString()
        : producto.stock.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0, useGrouping: true })
      : "0";

    const hayOferta = producto?.aofd_art_nprecio != null &&
      producto?.aofd_ofertaprecio != null &&
      producto.aofd_art_nprecio !== producto.aofd_ofertaprecio;

    const installment =producto?.stock > 0
        ? (hayOferta ? `Oferta: ${stockFormatted} u` : `Disponible: ${stockFormatted} u`)
        : "Sin Stock";

    const price = producto?.aofd_ofertaprecio != null
      ? "$" + producto.aofd_ofertaprecio.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "Precio no disponible";

    const priceOld = producto?.aofd_art_nprecio != null
      ? "$" + producto.aofd_art_nprecio.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : null;

    const percentage = producto?.aofd_art_nprecio > 0 && producto?.aofd_ofertaprecio != null
      ? (((producto.aofd_art_nprecio - producto.aofd_ofertaprecio) / producto.aofd_art_nprecio) * 100)
        .toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
      : null;

    const fractional= producto?.oferta_Art?.art_fraccionable === 1 ? true :false;

    return {
      id: producto?.oferta_Art?.id, code: producto?.oferta_Art?.art_codigo,
      category: producto?.oferta_Art?.Ramo?.descripcion_ramo ?? "Sin categoría",
      description: producto?.oferta_Art?.art_descripcion ?? "Sin descripción",
      section: "TrendingProducts", imgSrc: getImageSrc(producto),
      altText: producto?.oferta_Art?.art_descripcion ?? "Sin descripción",
      title: producto?.oferta_Art?.art_descripcion ?? "Sin título",
      price, priceOld, percentage, stockActual: stockFormatted, installment,
      fractional,
      availability: producto?.oferta_Art?.art_desclarga ?? "",
      rating: null, reviews: 580,
    };
  }, [empresa, getImageSrc]);

  const fetchOfertas = useCallback(async () => {
    setLoading(true);
    try {
      const cliente = JSON.parse(localStorage.getItem('clienteSelected'));
      const clienteId = cliente?.id ?? user?.id;
      const ord = getOrder(orderBy);

      const res = await getOfertasPaginate({
        searchTerm,
        selectedRamo: selectedRamoId,      // ← ID para el backend
        selectedRubro: selectedRubroId,
        selectedSubrubro: selectedSubrubroId,
        selectedMarca: selectedMarcaId,
        page: currentPage, perPage,
        user_id: clienteId,
        orderBy: ord.orderBy, orden: ord.orden,
        ofcab_id: ofertasId,
      });

      if (res && Array.isArray(res.ofertas_det)) {
        setProducts(res.ofertas_det.map(mapProducto));
        setTotalPages(res.pagination.totalPage);
        setTotalItems(res.pagination.totalItems);
      }
    } catch (e) {
      console.error("Error al obtener ofertas:", e);
    } finally {
      setLoading(false);
    }
  }, [ofertasId, searchTerm, selectedRamoId, selectedRubroId, selectedSubrubroId,
    selectedMarcaId, currentPage, perPage, orderBy, empresa, mapProducto, user?.id]);

  useEffect(() => { fetchOfertas(); }, [fetchOfertas]);

  const fetchAllProductsForPdf = async () => {
    try {
      const cliente = JSON.parse(localStorage.getItem('clienteSelected'));
      const clienteId = cliente?.id ?? user?.id;
      const base = {
        searchTerm, selectedRamo: selectedRamoId, selectedRubro: selectedRubroId,
        selectedSubrubro: selectedSubrubroId, selectedMarca: selectedMarcaId,
        user_id: clienteId, ofcab_id: ofertasId
      };

      const preview = await getOfertasPaginate({ ...base, page: 1, perPage: 1 });
      const total = preview?.pagination?.totalItems || 0;
      if (total === 0) return [];

      const full = await getOfertasPaginate({ ...base, page: 1, perPage: total });
      return full?.ofertas_det?.map(mapProducto) ?? [];
    } catch (e) { console.error(e); return []; }
  };

  const handleOpenPdf = async (format) => {
    setGenerandoPDF(true);
    try {
      const allProducts = await fetchAllProductsForPdf();
      if (!allProducts.length) return;
      const blob = await pdf(<ExportPdf products={allProducts} empresa={empresa} seccion="Ofertas"  format={format} />).toBlob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) { console.error(e); }
    finally { setGenerandoPDF(false); }
  };

  if (loading) return <Loading message="Cargando Ofertas..." />;

  return (
    <Grid container spacing={2} p={2} alignItems="center">
      <CategoryFilter
        ramoId={selectedRamoId}
        setRamoId={setRamoId}       // ← prop name coincide con lo que usa el componente
        rubroId={selectedRubroId}
        setRubroId={setRubroId}
        subrubroId={selectedSubrubroId}
        setSubrubroId={setSubrubroId}
        marcaId={selectedMarcaId}
        setMarcaId={setMarcaId}
        clearAll={clearAll}
      />
      <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="center">
        <ControlsRow
          isLogin={user?.id} products={products}
          generandoPDF={generandoPDF} setGenerandoPDF={setGenerandoPDF}
          handleOpenPdf={handleOpenPdf} viewMode={viewMode} setViewMode={setViewMode}
          orderBy={orderBy} setOrderBy={setOrderBy}
        />
      </Grid>
      <ProductList
        products={products} isCheckStock={empresa?.venta_sin_stock}
        viewMode={viewMode} totalPages={totalPages} currentPage={currentPage}
        perPage={perPage} totalItems={totalItems}
        handlePageChange={setCurrentPage} handlePerPageChange={setPerPage}
        cartItems={cartItems}
        toggleCartItem={(artId) => {
          const idCarrito = localStorage.getItem('idCarrito');
          if (!idCarrito) return;
          const exists = cartItems.find(i => i.cartId === idCarrito && i.artId === artId);
          if (exists) setCartItems(p => p.filter(i => i.cartId !== idCarrito || i.artId !== artId));
          else setCartItems(p => [...p, { cartId: Number(idCarrito), artId }]);
        }}
      />
    </Grid>
  );
};

export default OfertasResults;